/**
 * Renders testimonial block to a self-contained HTML string.
 * Used by BOTH the canvas renderer (via dangerouslySetInnerHTML) and the email HTML generator.
 * Single source of truth — guarantees canvas ↔ preview pixel-perfect parity.
 */

function escH(s: unknown): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function starsHtml(n: number): string {
  let s =
    '<table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="margin:0 auto;"><tr>';
  for (let i = 0; i < 5; i++) {
    s += `<td style="padding:0 2px;font-size:0;line-height:0;"><span style="font-size:16px;color:${i < n ? '#F59E0B' : '#D1D5DB'};line-height:1;font-family:Arial,sans-serif;">&#9733;</span></td>`;
  }
  s += '</tr></table>';
  return s;
}

function authorBlockHtml(
  t: Record<string, any>,
  textAlign: 'center' | 'left',
  ff: string
): string {
  const nameHtml = t.author
    ? `<div style="font-size:14px;font-weight:700;color:#111827;letter-spacing:0.04em;font-family:${ff};text-align:${textAlign};">${escH(t.author)}</div>`
    : '';
  const roleHtml = t.role
    ? `<div style="font-size:12px;color:#9CA3AF;margin-top:2px;font-family:${ff};text-align:${textAlign};">${escH(t.role)}</div>`
    : '';

  if (!t.image) {
    return `<div style="text-align:${textAlign};">${nameHtml}${roleHtml}</div>`;
  }

  const imgHtml = `<img src="${escH(t.image)}" alt="${escH(t.author || '')}" width="44" height="44" style="width:44px;height:44px;border-radius:50%;display:block;border:2px solid #F1F5F9;" />`;
  const align = textAlign === 'center' ? 'center' : 'left';
  return `
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="${align}" style="margin:${textAlign === 'center' ? '0 auto' : '0'};">
      <tr>
        <td valign="middle" style="padding-right:12px;">${imgHtml}</td>
        <td valign="middle">${nameHtml}${roleHtml}</td>
      </tr>
    </table>`;
}

export function renderTestimonialHtml(
  content: Record<string, any>,
  styles: Record<string, any>,
  fontFamily: string,
  isMobile: boolean
): string {
  const layout: string = (content.layout as string) || 'single';
  const testimonials: Record<string, any>[] = Array.isArray(content.testimonials)
    ? content.testimonials.filter((t: any) => t && t.enabled !== false)
    : [];
  const bg = (styles.backgroundColor as string) || '#FFFFFF';
  const ff = fontFamily || 'Arial, sans-serif';
  const serifFf = "Georgia, 'Times New Roman', serif";

  // Inline style wrapper — sets a known base so inherited styles from the builder shell
  // don't bleed in. Every child element also has explicit inline styles.

  // ── Section title row ─────────────────────────────────────────
  const titleRow = content.sectionTitle
    ? `<tr>
        <td align="center" style="padding:${isMobile ? '36px 24px 12px' : '48px 40px 16px'};text-align:center;background-color:${bg};">
          <h2 style="margin:0;padding:0;font-size:${isMobile ? '22px' : '28px'};font-weight:800;color:#0F172A;font-family:${serifFf};letter-spacing:-0.02em;line-height:1.2;">${escH(content.sectionTitle)}</h2>
        </td>
      </tr>`
    : '';

  // ── SINGLE layout ─────────────────────────────────────────────
  if (layout === 'single' || layout === 'standard') {
    if (!testimonials.length) return `<div style="background-color:${bg};padding:20px;">&nbsp;</div>`;
    const t = testimonials[0];
    const pad = isMobile ? '20px 28px 36px' : '28px 56px 48px';

    return `<div style="background-color:${bg};font-family:${ff};font-size:16px;line-height:normal;margin:0;padding:0;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:${bg};">
        ${titleRow}
        <tr>
          <td align="center" style="padding:${pad};text-align:center;background-color:${bg};">
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="${isMobile ? 'width:100%;' : 'max-width:600px;margin:0 auto;'}">
              <tr>
                <td align="center" style="text-align:center;width:100%;">
                  ${t.rating ? `<div style="margin-bottom:20px;">${starsHtml(t.rating)}</div>` : ''}
                  <div style="font-size:52px;line-height:0.8;color:#D1D5DB;font-family:${serifFf};margin-bottom:16px;text-align:center;">&#8220;</div>
                  ${t.quote ? `<p style="font-size:${isMobile ? '17px' : '19px'};line-height:1.7;color:#1E293B;font-family:${serifFf};font-style:italic;margin:0 0 24px 0;text-align:center;font-weight:400;word-wrap:break-word;">${escH(t.quote)}</p>` : ''}
                  <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="margin:0 auto 20px auto;">
                    <tr><td width="40" height="2" bgcolor="#E2E8F0" style="font-size:0;line-height:0;width:40px;">&nbsp;</td></tr>
                  </table>
                  ${authorBlockHtml(t, 'center', ff)}
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </div>`;
  }

  // ── GRID layout ───────────────────────────────────────────────
  if (layout === 'grid') {
    if (!testimonials.length) return `<div style="background-color:${bg};padding:20px;">&nbsp;</div>`;
    const items = testimonials.slice(0, 3);
    const cols = isMobile ? 1 : Math.min(items.length, 3);
    const colW = Math.floor(100 / cols);
    const pad = isMobile ? '12px 16px 36px' : '16px 32px 44px';

    const cardHtml = (t: Record<string, any>) => {
      const qHtml = t.quote
        ? `<p style="font-size:${isMobile ? '15px' : '14px'};line-height:1.65;color:#374151;font-family:${ff};margin:0 0 18px 0;">&#8220;${escH(t.quote)}&#8221;</p>`
        : '';
      return `<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:#F8FAFC;border:1px solid #E8EDF5;border-radius:12px;">
        <tr>
          <td style="padding:24px;">
            ${t.rating ? `<div style="margin-bottom:12px;">${starsHtml(t.rating)}</div>` : ''}
            ${qHtml}
            ${authorBlockHtml(t, 'left', ff)}
          </td>
        </tr>
      </table>`;
    };

    let gridBody: string;
    if (isMobile) {
      // Mobile: each card in its own row, full width, with gap between
      gridBody = items.map((t, idx) => `
        <tr>
          <td style="padding-bottom:${idx < items.length - 1 ? '12px' : '0'};">
            ${cardHtml(t)}
          </td>
        </tr>`).join('');
    } else {
      // Desktop: all cards in one row
      const cells = items.map((t) =>
        `<td valign="top" width="${colW}%" style="width:${colW}%;padding:0 8px 0;vertical-align:top;">${cardHtml(t)}</td>`
      );
      // Fill empty columns
      for (let i = items.length; i < cols; i++) {
        cells.push(`<td width="${colW}%" style="width:${colW}%;"></td>`);
      }
      gridBody = `<tr>${cells.join('')}</tr>`;
    }

    return `<div style="background-color:${bg};font-family:${ff};font-size:16px;line-height:normal;margin:0;padding:0;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:${bg};">
        ${titleRow}
        <tr>
          <td style="padding:${pad};background-color:${bg};">
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
              ${gridBody}
            </table>
          </td>
        </tr>
      </table>
    </div>`;
  }

  // ── CREATIVE-STAR layout ──────────────────────────────────────
  if (layout === 'creative-star') {
    if (!testimonials.length) return `<div style="background-color:${bg};padding:20px;">&nbsp;</div>`;
    const t = testimonials[0];
    const gradBg = '#F8FAFC';
    const pad = isMobile ? '44px 24px' : '64px 40px';
    const imgSize = isMobile ? 80 : 96;

    return `<div style="background-color:${gradBg};font-family:${ff};font-size:16px;line-height:normal;margin:0;padding:0;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:${gradBg};">
        ${titleRow}
        <tr>
          <td align="center" style="padding:${pad};text-align:center;background-color:${gradBg};">
            ${t.image ? `<div style="margin-bottom:20px;"><img src="${escH(t.image)}" alt="${escH(t.author || '')}" width="${imgSize}" height="${imgSize}" style="width:${imgSize}px;height:${imgSize}px;border-radius:50%;display:inline-block;border:3px solid #FFFFFF;" /></div>` : ''}
            ${t.rating ? `<div style="margin-bottom:16px;">${starsHtml(t.rating)}</div>` : ''}
            ${t.quote ? `<p style="font-size:${isMobile ? '18px' : '21px'};line-height:1.7;color:#1E293B;font-family:${serifFf};font-style:italic;max-width:580px;margin:0 auto 24px auto;font-weight:400;text-align:center;word-wrap:break-word;">&#8220;${escH(t.quote)}&#8221;</p>` : ''}
            ${t.author ? `<div style="font-size:14px;font-weight:700;color:#111827;letter-spacing:0.04em;font-family:${ff};text-align:center;margin-bottom:2px;">${escH(t.author)}</div>` : ''}
            ${t.role ? `<div style="font-size:12px;color:#94A3B8;font-family:${ff};text-align:center;">${escH(t.role)}</div>` : ''}
          </td>
        </tr>
      </table>
    </div>`;
  }

  return `<div style="background-color:${bg};padding:20px;">&nbsp;</div>`;
}
